import { useTheme } from "@mui/material";
import { htmlToSlate, payloadSlateToHtmlConfig, slateToHtml } from "@slate-serializers/html";
import React, { useMemo, useState } from "react";
import { toast } from "react-toastify";
import { createEditor } from "slate";
import { withHistory } from "slate-history";
import { withReact } from "slate-react";

import { StyledTextEditorContainer } from "../../../../Components/v2/atoms/StyledTextEditorContainer/StyledTextEditorContainer";
import ActivityFeed from "../../../../Components/v2/organism/ActivityFeed/ActivityFeed";
import ScenarioCommentActivity from "../../../../Components/v2/organism/ActivityItems/Comment";
import { ScenarioCreateActivity } from "../../../../Components/v2/organism/ActivityItems/ScenarioCreateActivity";
import { ScenarioDuplicateActivity } from "../../../../Components/v2/organism/ActivityItems/ScenarioDuplicateActivity";
import { ScenarioSubmittedActivity } from "../../../../Components/v2/organism/ActivityItems/ScenarioSubmittedActivity";
import { StatusChangeActivity } from "../../../../Components/v2/organism/ActivityItems/StatusChangeActivity";
import { Activity } from "../../../../Components/v2/organism/ActivityItems/types";
import RichTextEditor from "../../../../Components/v2/organism/RichTextEditor/RichTextEditor";
import { ActivityLogDTO, useInfiniteActivityLogs } from "../../api/getActivityLog";
import { ActivityEventEnum } from "../../types";
import { clearAndResetEditor } from "../../utils/clearAndResetEditor";
import { convertToActivity } from "../../utils/convertToActivity";
import { isEditorContentEmpty } from "../../utils/isEditorContentEmpty";
import { useDeleteComment } from "../api/ActivityLog/deleteComments";
import { useUpdateComment } from "../api/ActivityLog/updateComments";
import { useActivityLogV2 } from "../api/v2/getActivityLog";
import { DeleteCommentModal } from "../components/DeleteCommentModal/DeleteCommentModal";

interface ScenarioActivityFeedProps {
  scenarioId: string;
  scenarioName: string;
  email: string | undefined;
  isScenariosApiEnabled: boolean;
}

const ScenarioActivityFeed: React.FC<ScenarioActivityFeedProps> = ({
  scenarioId,
  scenarioName,
  email,
  isScenariosApiEnabled,
}) => {
  const theme = useTheme();
  const editor = useMemo(() => {
    const ed = withHistory(withReact(createEditor()));
    // Initialize with proper content structure
    ed.children = [{ children: [{ text: "" }] }];
    return ed;
  }, []);
  const [editingActivityId, setEditingActivityId] = useState<string | null>(null);
  const [deleteCommentModalOpen, setDeleteCommentModalOpen] = useState(false);
  const [commentToDelete, setCommentToDelete] = useState<Activity | null>(null);

  const COMMENT_LIMIT = 12;
  const REFRESH_INTERVAL = 1000 * 60 * 2; // 2 minutes

  const {
    data: activityLogDataV1,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading: isLoadingV1,
  } = useInfiniteActivityLogs({
    scenarioId,
    limit: COMMENT_LIMIT,
    refetchInterval: REFRESH_INTERVAL,
    enabled: !isScenariosApiEnabled,
  });

  const { data: activityLogDataV2, isLoading: isLoadingV2 } = useActivityLogV2({
    scenarioId,
    refetchInterval: REFRESH_INTERVAL,
    enabled: isScenariosApiEnabled,
  });

  const activityLogs: ActivityLogDTO[] = isScenariosApiEnabled
    ? activityLogDataV2 || []
    : activityLogDataV1?.pages.flatMap((page: any) => page.events) || [];

  const isLoading = isScenariosApiEnabled ? isLoadingV2 : isLoadingV1;

  // Deduplicate activity logs based on UUID
  const uniqueActivityLogs = useMemo(() => {
    const seenUuids = new Set();
    return activityLogs.filter((activity) => {
      if (seenUuids.has(activity.event.eventUuid)) {
        console.error(`Duplicate event detected with UUID: ${activity.event.eventUuid}`);
        return false;
      }
      seenUuids.add(activity.event.eventUuid);
      return true;
    });
  }, [activityLogs]);

  const convertedActivityLogs = uniqueActivityLogs
    .map((activity) => convertToActivity(activity, theme, scenarioName))
    .filter((activity) => activity !== null) as Activity[];

  const { mutate: updateComment } = useUpdateComment({ isScenariosApiEnabled });

  const { mutate: deleteComment } = useDeleteComment({
    scenarioId: scenarioId,
    isScenariosApiEnabled,
  });

  const handleEdit = (activity: Activity) => {
    clearAndResetEditor(editor);
    setEditingActivityId(activity.id);
  };

  const handleCancelEdit = () => {
    setEditingActivityId(null);
    clearAndResetEditor(editor);
  };

  const handleDelete = (activity: Activity) => {
    setCommentToDelete(activity);
    setDeleteCommentModalOpen(true);
  };

  const confirmDeleteComment = () => {
    if (commentToDelete) {
      deleteComment(commentToDelete.id);
      setDeleteCommentModalOpen(false);
      setCommentToDelete(null);
    }
  };

  const handleUpdateComment = (activity: Activity) => {
    const htmlContent = slateToHtml(editor.children, payloadSlateToHtmlConfig);

    if (isEditorContentEmpty(editor)) {
      toast.error("Comment cannot be empty");
      return;
    }

    updateComment({
      scenarioId: scenarioId,
      commentId: activity.id,
      commentData: htmlContent,
    });

    clearAndResetEditor(editor);

    handleCancelEdit();
  };

  const getActivityContent = (activity: Activity) => {
    if (
      editingActivityId === activity.id &&
      activity.eventType === ActivityEventEnum.ScenarioCommentAdded
    ) {
      const htmlContent = activity.data.htmlContent;
      const slateContent = htmlToSlate(htmlContent);
      return (
        <ScenarioCommentActivity
          theme={theme}
          isEditable={email === activity.user.email}
          isEditing={true}
          key={activity.id}
          activity={activity}
          initials={activity?.user.initials}
          name={activity.user.name}
          handleClose={handleCancelEdit}
        >
          <RichTextEditor
            toolbarElements={["bold", "italic", "underline"]}
            editor={editor}
            initialValue={slateContent}
            onSubmit={() => {
              handleUpdateComment(activity);
            }}
          />
        </ScenarioCommentActivity>
      );
    }

    switch (activity.eventType) {
      case ActivityEventEnum.ScenarioStatusUpdated:
        return <StatusChangeActivity key={activity.id} activity={activity} />;
      case ActivityEventEnum.ScenarioDuplicated:
        return <ScenarioDuplicateActivity key={activity.id} activity={activity} />;
      case ActivityEventEnum.ScenarioCreated:
        return <ScenarioCreateActivity key={activity.id} activity={activity} />;
      case ActivityEventEnum.ScenarioSubmitted:
        return <ScenarioSubmittedActivity key={activity.id} activity={activity} />;
      case ActivityEventEnum.ScenarioCommentAdded:
        return (
          <ScenarioCommentActivity
            theme={theme}
            // This check won't work for new SBO API, ensure that it does when the backend returns the correct email.
            isEditable={email === activity.user.email}
            isEditing={false}
            key={activity.id}
            activity={activity}
            initials={activity.user.initials}
            name={activity.user.name}
            handleEdit={() => handleEdit(activity)}
            handleDelete={() => handleDelete(activity)}
            handleClose={handleCancelEdit}
          >
            <StyledTextEditorContainer>
              <div dangerouslySetInnerHTML={{ __html: activity.data.htmlContent }} />
            </StyledTextEditorContainer>
          </ScenarioCommentActivity>
        );
      default:
        return null;
    }
  };

  const activities = convertedActivityLogs.map((activity: Activity) => ({
    id: activity.id,
    createdAt: activity.createdAt,
    content: getActivityContent(activity),
    isEditable: activity.eventType === ActivityEventEnum.ScenarioCommentAdded,
  }));

  return (
    <>
      {/* TODO: Implement virtualized activity feed. Current implementation has issues with adding/deleting activities due to grouping logic. */}
      {/* To reproduce the issue, the easiest way is to update the dates and use a timestamp then add/delete comments this affects the height of the activity feed and elements overlap */}
      <ActivityFeed
        activities={activities}
        isLoading={isLoading}
        isFetchingNextPage={isScenariosApiEnabled ? false : isFetchingNextPage}
        hasNextPage={isScenariosApiEnabled ? false : hasNextPage}
        fetchNextPage={isScenariosApiEnabled ? () => {} : fetchNextPage}
        nextCursor={
          isScenariosApiEnabled
            ? null
            : (activityLogDataV1?.pages[activityLogDataV1.pages.length - 1]?.nextCursor ?? null)
        }
      />
      <DeleteCommentModal
        open={deleteCommentModalOpen}
        onClose={() => setDeleteCommentModalOpen(false)}
        onConfirm={confirmDeleteComment}
      />
    </>
  );
};

export default ScenarioActivityFeed;
